// cleanup_convert_to_typescript_file_CREATE_EMPLOYEE_PAYMENTS
// cleanup_remove_external_libraries_CREATE_EMPLOYEE_PAYMENTS

import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { useHistory } from 'react-router';
import moment from 'moment';
import { template } from 'lodash';

import { Button, PRIMARY, SECONDARY } from '@glu/buttons-react';

import AppPropTypes from 'proptypes/AppPropTypes';
import { URL_PARAM_ACTIONS, WORKFLOWS, WORKSPACES } from 'constants.js';
import { numberToCurrency } from 'utils/currency';

import APIDataShape from './APIDataShape';
import useStyles from './employeePayments.styles';

const { COPY, CREATE, MODIFY, VIEW } = URL_PARAM_ACTIONS;
const { MODIFY_EMPLOYEE_PAYMENT } = WORKFLOWS;
const { PYMTCNTR } = WORKSPACES;

const Footer = ({
  APIData,
  action,
  amount,
  canSubmit,
  locale,
  numPayments,
  onSubmit,
  possibleActions,
  setConfirmationAction,
  setMessage,
  validate,
  values
}) => {
  const history = useHistory();
  const { commonButton, flexWithCenterSpan, footer, footerCell, footerFlexRow, total } =
    useStyles();
  const selectedEntity = values?.batch?.entity;
  const entityApiData = selectedEntity?.length
    ? APIData?.achCompanies.find((record) => record.achSenderId === selectedEntity?.[0]?.id)
    : '';
  const approvalRequired = entityApiData?.approvalRequired;
  const isView = action === VIEW;

  return (
    <article className={`col-lg-12  ${footer} ${footerFlexRow}`}>
      <div className={footerCell}>
        <span className={total}>{numPayments}</span>
        <span className={flexWithCenterSpan}>
          <span>{locale['common.employee']}</span>
          <span>{locale['common.payments']}</span>
        </span>
      </div>
      <span>
        <span className={`${total} ${footerCell}`}>
          {amount ? `${numberToCurrency(amount)}` : '$0.00'}
        </span>
      </span>
      {[CREATE, MODIFY, COPY].includes(action) ? (
        <Button
          className={commonButton}
          onClick={async () => {
            setConfirmationAction('submit');
            const { data, isValid } = await validate();
            // if effectEntryDate is after earliest Day for ACH
            const effectiveEntryDate = data?.batch?.effectiveEntryDate?.[0];
            const earliestDayForAch = APIData?.dateSettings?.startDate;
            const isValidDate =
              effectiveEntryDate && earliestDayForAch
                ? effectiveEntryDate.isSameOrAfter(moment(earliestDayForAch), 'day')
                : false;

            if (isValid && isValidDate) return onSubmit('send');
            if (!isValidDate) {
              const errorMsg = template(locale['employee.date.past.error'])({
                earliestDayForAch: moment(earliestDayForAch).format('MM/DD/YYYY')
              });
              return setMessage({ success: false, title: errorMsg });
            }
            return null;
          }}
          dataQa="send"
          text={approvalRequired ? locale['common.submitForApproval'] : locale['common.send']}
          disabled={!canSubmit}
          variant={PRIMARY}
        />
      ) : null}
      {[VIEW, MODIFY].includes(action) && possibleActions.includes('CopyAsPayment') && (
        <Button
          className={commonButton}
          onClick={async () => {
            setConfirmationAction('copy');
            const { isValid } = await validate();
            if (isValid) onSubmit('copy');
          }}
          dataQa="copy-as-payment"
          text={locale['common.copyAsPayment']}
          variant={PRIMARY}
        />
      )}
      {isView && possibleActions.includes('Approve') && (
        <Button
          className={commonButton}
          onClick={() => {
            setConfirmationAction('approve');
            onSubmit('approve');
          }}
          data-qa="approve"
          text={locale['common.approve']}
          variant={SECONDARY}
        />
      )}
      {isView && possibleActions.includes('Modify') && (
        <Button
          className={commonButton}
          onClick={() => {
            history.push({
              pathname: `${MODIFY_EMPLOYEE_PAYMENT}/${APIData?.paymentDetails?.custTrace}`
            });
          }}
          dataQa="modify"
          text={locale['common.modify']}
          variant={SECONDARY}
        />
      )}
      {isView && possibleActions.includes('Reject') && (
        <Button
          className={commonButton}
          onClick={() => {
            setConfirmationAction('reject');
            onSubmit('reject');
          }}
          dataQa="reject"
          text={locale['common.reject']}
          variant={SECONDARY}
        />
      )}
      {isView && possibleActions.includes('Delete') && (
        <Button
          className={commonButton}
          onClick={() => {
            setConfirmationAction('delete');
            onSubmit('delete');
          }}
          dataQa="delete"
          text={locale['common.delete']}
          variant={SECONDARY}
        />
      )}
      <Button
        className={commonButton}
        onClick={() => {
          setConfirmationAction('back');
          if (!APIData.achCompany) {
            history.push({
              pathname: history?.location?.state?.previous || PYMTCNTR
            });
          } else {
            onSubmit('back');
          }
        }}
        dataQa="back"
        text={locale['common.back']}
        variant={SECONDARY}
      />
    </article>
  );
};

Footer.propTypes = {
  APIData: APIDataShape.isRequired,
  action: PropTypes.string.isRequired,
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  canSubmit: PropTypes.bool.isRequired,
  history: PropTypes.shape({}),
  locale: AppPropTypes.locale.isRequired,
  numPayments: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSubmit: PropTypes.func.isRequired,
  possibleActions: PropTypes.arrayOf(PropTypes.string),
  setConfirmationAction: PropTypes.func,
  setMessage: PropTypes.func.isRequired,
  validate: PropTypes.func.isRequired,
  values: PropTypes.shape({}).isRequired
};

Footer.defaultProps = {
  history: {
    push() {}
  },
  possibleActions: [],
  setConfirmationAction: () => {}
};
export default withRouter(Footer);
